<script lang="ts">
    import Vue from "vue";
    import { Component } from 'vue-facing-decorator';
    import { EditorState } from "@common/Common";
    import { RawCriterionWeight } from "@Criteria/Model";
    import { WeightsSetEditorCreateWeightsSetEventPayload, WeightsSetEditorCriterionViewModel, WeightsSetEditorNormalizeWeightsEventPayload, WeightsSetEditorUpdateWeightsSetEventPayload, WeightsSetEditorViewModel } from "@Criteria/Weights/Components/WeightsSetEditor";


    @Component({
        props: {
            viewModel: {
                type: Object,
                required: true
            }
        }
    })
    export default class WeightsSetEditor extends Vue
    {
        get vm(): WeightsSetEditorViewModel { return this.$props.viewModel as WeightsSetEditorViewModel; }

        get opened(): boolean { return this.vm?.editorState !== EditorState.Closed; }
        get initialized(): boolean { return this.vm.initialized; }
        get criteria(): WeightsSetEditorCriterionViewModel[] { return this.vm.criteria; }
        get nameError(): string | null { return this.vm.errors.find(e => e.ErrorCode.startsWith("Name."))?.Error ?? null; }
        get hasDirtyWeights(): boolean { return this.criteria.some(c => c.localWeight !== c.inputValue); }
        get title(): string | null
        {
            if (this.vm.editorState === EditorState.Creator)
            {
                return "Create weights set"
            }
            else if (this.vm.editorState === EditorState.Editor)
            {
                return "Edit weights set"
            }

            return null;
        }

        getError(criterionId: number): string | null
        {
            if (this.vm.errors?.length)
            {
                const err = this.vm.errors.find(e => e.Context === criterionId.toString());
                return err?.Error ?? null;
            }
            
            return null;
        }


        emitFormSubmit()
        {
            this.clearValidationErrors();
            const weights: RawCriterionWeight[]  = this.criteria.map(c =>  { return { CriterionId: c.id, LocalWeight: c.inputValue }});

            if (this.vm.editorState === EditorState.Creator)
            {
                this.$emit("create-weights-set", new WeightsSetEditorCreateWeightsSetEventPayload(this.vm.name, weights));
            }
            else if (this.vm.editorState === EditorState.Editor)
            {
                this.$emit("update-weights-set", new WeightsSetEditorUpdateWeightsSetEventPayload(this.vm.weightsSetId, this.vm.name, weights));
            }
        }

        emitNormalizeWeights()
        {
            const weights: RawCriterionWeight[]  = this.criteria.map(c =>  { return { CriterionId: c.id, LocalWeight: c.inputValue }});
            
            this.$emit("normalize-weights", new WeightsSetEditorNormalizeWeightsEventPayload(weights));
        }

        clearValidationErrors()
        {
            this.vm.errors = [];
        }

        close()
        {
            this.vm.close();
        }
    }
</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{title}}</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <h3 v-if="!initialized"><i class="fa fa-spinner fa-spin"></i> Loading... </h3>

                        <div v-if="initialized">


                            <form id="weights-set-editor-form" @submit.prevent="emitFormSubmit">
                                <div class="form-group">
                                    <label>Name:</label>
                                    <input v-model.trim="vm.name" type="text" class="form-control" required minlength="2" maxlength="100">
                                    <div class="error">{{nameError}}</div>
                                </div>

                                <br />
                                <table class="table table-bordered table-hover">
                                    <thead>
                                        <tr>
                                            <th rowspan="2">Criterion</th>
                                            <th rowspan="2" style="width: 150px;">Input</th>
                                            <th colspan="2">Normalized weight [%]</th>
                                        </tr>
                                        <tr>
                                            <th style="width: 150px;">Local</th>
                                            <th style="width: 150px;">Global</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="c in criteria" :key="c.id">
                                            <td>
                                                {{c.name}}<br /><small>{{c.path}}</small>
                                            </td>
                                            <td>
                                                <input v-if="c.isGoal" type="number" disabled value="100" class="form-control" />
                                                <input v-else type="number" v-model.number="c.inputValue" min="0" step="any" class="form-control" />
                                                <div class="error">{{getError(c.id)}}</div>
                                            </td>
                                            <td>
                                                <span v-if="c.isGoal">100</span>
                                                <span v-else>
                                                    <span v-if="!hasDirtyWeights">
                                                        {{ c.localWeight }}
                                                    </span>
                                                </span>
                                            </td>
                                            <td>
                                                <span v-if="c.isGoal">100</span>
                                                <span v-else>
                                                    <span v-if="!hasDirtyWeights">
                                                        {{ c.globalWeight }}
                                                    </span>
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </form>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                        <button type="button" v-if="hasDirtyWeights" @click="emitNormalizeWeights" class="btn btn-warning">Normalize</button>
                        <button type="submit" class="btn btn-primary" form="weights-set-editor-form">Save</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
</style>